import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, MapPin } from 'lucide-react';

const teeTimes = [
  {
    day: 'Friday',
    course: 'Top of the Rock',
    date: '2026-07-17',
    mapsQuery: 'Top of the Rock Golf Course, Ridgedale, MO',
    groups: [
      { time: '12:30 PM', players: ['Andy', 'Zac', 'Brittany', 'Chris'] },
      { time: '12:45 PM', players: ['Gardner', 'Jason', 'Tyler', 'Ben'] },
      { time: '1:00 PM', players: ['Ethan', 'Joe', 'Peggy', 'Tom'] },
      { time: '1:15 PM', players: ['Russ', 'Dave', 'Courtney', 'Steve'] }
    ]
  },
  {
    day: 'Saturday',
    course: "Payne's Valley",
    date: '2026-07-18',
    mapsQuery: "Payne's Valley Golf Course, Ridgedale, MO",
    groups: [
      { time: '6:30 AM', players: ['Ben', 'Chris', 'Courtney', 'Steve'] },
      { time: '6:40 AM', players: ['Andy', 'Jason', 'Peggy', 'Dave'] },
      { time: '6:50 AM', players: ['Gardner', 'Joe', 'Brittany', 'Russ'] },
      { time: '7:00 AM', players: ['Ethan', 'Zac', 'Tyler', 'Tom'] }
    ]
  },
  {
    day: 'Sunday',
    course: 'Cliffhangers',
    date: '2026-07-19',
    mapsQuery: 'Cliffhangers Golf Course, Big Cedar Lodge, Ridgedale, MO',
    groups: [
      { time: '8:45 AM', players: ['Andy', 'Joe', 'Tyler', 'Courtney'] },
      { time: '9:00 AM', players: ['Ethan', 'Jason', 'Brittany', 'Tom'] },
      { time: '9:15 AM', players: ['Ben', 'Chris', 'Dave', 'Russ'] },
      { time: '9:30 AM', players: [{ name: 'Gardner', out: true }, 'Zac', 'Peggy', 'Steve'] }
    ]
  }
];

// NWS gridpoint for Big Cedar Lodge / Ridgedale, MO (36.52, -93.24)
const WEATHER_URL = 'https://api.weather.gov/gridpoints/SGF/69,4/forecast';

const isPrecipForecast = (f) =>
  f.includes('thunder') || f.includes('rain') || f.includes('showers') || f.includes('drizzle');

const weatherEmoji = (shortForecast, precip) => {
  let f = shortForecast.toLowerCase();
  if (precip < 30 && isPrecipForecast(f)) {
    // Low chance of rain: show the dry half of NWS's "X then Y" phrasing instead
    const dry = f.split(' then ').find((clause) => !isPrecipForecast(clause));
    f = dry || 'partly sunny';
  }
  if (f.includes('thunder')) return '⛈️';
  if (f.includes('rain') || f.includes('showers')) return '🌧️';
  if (f.includes('drizzle')) return '🌦️';
  if (f.includes('snow') || f.includes('sleet') || f.includes('ice')) return '❄️';
  if (f.includes('fog') || f.includes('haze')) return '🌫️';
  if (f.includes('mostly sunny') || f.includes('partly')) return '🌤️';
  if (f.includes('sunny') || f.includes('clear')) return '☀️';
  if (f.includes('cloudy')) return '☁️';
  return '🌡️';
};

const parseFirstTee = ({ date, groups }) => {
  const [time, meridiem] = groups[0].time.split(' ');
  let [hours, minutes] = time.split(':').map(Number);
  if (meridiem === 'PM' && hours !== 12) hours += 12;
  if (meridiem === 'AM' && hours === 12) hours = 0;
  const pad = (n) => String(n).padStart(2, '0');
  return new Date(`${date}T${pad(hours)}:${pad(minutes)}:00`);
};

const dayStatus = (day, now) => {
  const firstTee = parseFirstTee(day);
  const dayEnd = new Date(`${day.date}T23:59:59`);
  if (now > dayEnd) return { state: 'past' };
  if (now >= firstTee) return { state: 'live' };
  return { state: 'upcoming', firstTee };
};

const formatCountdown = (ms) => {
  const totalMinutes = Math.ceil(ms / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const GolfApp = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [now, setNow] = useState(() => new Date());
  const [weather, setWeather] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = new Audio('/takes-a-lot-of-balls.mp3');
    audio.loop = false;
    audio.volume = 0.5;
    audio.addEventListener('ended', () => setIsPlaying(false));
    audioRef.current = audio;

    return () => {
      audio.pause();
      audioRef.current = null;
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetch(WEATHER_URL, { headers: { Accept: 'application/geo+json' } })
      .then((response) => response.json())
      .then((data) => {
        const periods = data.properties && data.properties.periods;
        if (!periods) return;
        const byDate = {};
        teeTimes.forEach(({ date }) => {
          const day = periods.find((p) => p.isDaytime && p.startTime.startsWith(date));
          const night = periods.find((p) => !p.isDaytime && p.startTime.startsWith(date));
          if (!day) return;
          byDate[date] = {
            forecast: day.shortForecast,
            high: day.temperature,
            low: night ? night.temperature : null,
            precip: (day.probabilityOfPrecipitation && day.probabilityOfPrecipitation.value) || 0
          };
        });
        setWeather(byDate);
      })
      .catch((error) => {
        console.error('Error fetching weather:', error);
      });
  }, []);

  const toggleMusic = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(error => {
        console.error('Error playing audio:', error);
      });
    }
    setIsPlaying(!isPlaying);
  };

  const togglePlayer = (name) => {
    setSelectedPlayer((current) => (current === name ? null : name));
  };

  const upNextDay = teeTimes.find((day) => dayStatus(day, now).state !== 'past');

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-4xl font-bold text-black mb-2">2026 TruckMovers Invitational</h1>
          <p className="text-gray-600">Tee Times</p>
        </div>

        {/* Music Player */}
        <div className="w-full mx-auto mb-8">
          <div className="flex items-center justify-around gap-2 px-4 py-2 bg-gradient-to-br from-orange-50 via-orange-100 to-orange-200 hover:from-orange-100 hover:via-orange-200 hover:to-orange-300 rounded-lg border-2 border-orange-400 text-orange-900">
            <img
              src="/albumart.png"
              alt="Album art for It Takes a lot of Balls"
              className="w-20 h-20 rounded-lg shadow-md object-cover"
            />
            <button
              onClick={toggleMusic}
              className="flex items-center gap-2"
            >
              {isPlaying ? <Pause size={20} /> : <Play size={20} />}
              {isPlaying ? 'Pause' : 'Play'}
            </button>
            <div className="text-sm text-orange-800 border-l border-orange-400 pl-3">
              <div className="font-medium">Takes a lot of Balls</div>
              <div className="text-orange-700">by Sir Shanks-a-Lot aka Russ D</div>
            </div>
          </div>
        </div>

        {/* Tee Times */}
        <div className="space-y-8">
          {teeTimes.map((day) => {
            const { day: dayName, course, date, mapsQuery, groups } = day;
            const status = dayStatus(day, now);
            const isUpNext = upNextDay && upNextDay.day === dayName;
            const dayWeather = weather && weather[date];

            return (
              <div
                key={dayName}
                className={`bg-white rounded-lg shadow-lg overflow-hidden ${
                  status.state === 'past' ? 'opacity-60' : ''
                } ${isUpNext ? 'ring-2 ring-green-500' : ''}`}
              >
                <div className="bg-gray-800 text-white p-4 flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-xl font-bold">{dayName} — {course}</h2>
                  <div className="flex items-center gap-4 text-sm">
                    {dayWeather && (
                      <span className="text-gray-200" title={dayWeather.forecast}>
                        {weatherEmoji(dayWeather.forecast, dayWeather.precip)} {dayWeather.high}°{dayWeather.low !== null ? `/${dayWeather.low}°` : ''} · {dayWeather.precip}%💧
                      </span>
                    )}
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsQuery)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-green-300 hover:text-green-200"
                    >
                      <MapPin size={16} />
                      Directions
                    </a>
                  </div>
                </div>
                {status.state === 'upcoming' && (
                  <div className={`px-4 py-2 text-sm font-medium ${
                    isUpNext ? 'bg-green-50 text-green-800' : 'bg-gray-100 text-gray-600'
                  }`}>
                    ⏳ First tee in {formatCountdown(status.firstTee - now)}
                    {isUpNext && <span className="ml-2 text-xs font-bold uppercase tracking-wide bg-green-600 text-white rounded px-2 py-0.5">Up next</span>}
                  </div>
                )}
                {status.state === 'live' && (
                  <div className="px-4 py-2 text-sm font-medium bg-green-50 text-green-800">
                    ⛳ On the course now
                  </div>
                )}
                <table className="w-full">
                  <tbody>
                    {groups.map(({ time, players }) => {
                      const names = players.map((p) => (typeof p === 'string' ? p : p.name));
                      const hasSelected = selectedPlayer && names.includes(selectedPlayer);
                      return (
                        <tr
                          key={time}
                          className={`border-b last:border-b-0 ${
                            hasSelected ? 'bg-green-50' : selectedPlayer ? 'opacity-40' : 'hover:bg-gray-50'
                          }`}
                        >
                          <td className="px-4 py-3 font-bold text-gray-900 whitespace-nowrap">{time}</td>
                          {players.map((player, i) => {
                            const name = typeof player === 'string' ? player : player.name;
                            const out = typeof player === 'object' && player.out;
                            const isSelected = selectedPlayer === name;
                            return (
                              <td key={i} className="px-2 py-2 text-center">
                                <button
                                  onClick={() => togglePlayer(name)}
                                  className={`px-2 py-1 rounded ${
                                    out ? 'line-through text-gray-400' : 'text-gray-700'
                                  } ${isSelected ? 'bg-green-600 text-white font-semibold' : ''}`}
                                >
                                  {name}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default GolfApp;
